import { useEffect, useState } from "react";
import { api, formatCents } from "@/lib/api";
import { PageHeader, Card, Loader } from "@/components/ui/Primitives";
import {
  Wallet, Building2, FileText, Wrench, Sparkles, TrendingUp, AlertCircle,
  CheckCircle2, RefreshCw, Heart, Users2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * My Business — landlord-scoped real-time business workspace.
 * Same KPI shape as /internal but every number is from the requesting
 * landlord's own portfolio (auth-scoped backend).
 */
export default function MyBusiness() {
  const [d, setD] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [ov, ts] = await Promise.all([
        api.get("/my-business/overview"),
        api.get("/my-business/timeseries?days=30"),
      ]);
      setD(ov.data);
      setSeries(ts.data);
    } catch {
      toast.error("Couldn't load your business snapshot");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);
  if (loading) return <Loader />;
  if (!d) return null;

  const p = d.portfolio; const c = d.cash; const e = d.engagement;
  return (
    <div data-testid="my-business-page">
      <PageHeader
        title="My Business"
        subtitle="Your live portfolio snapshot — no caching, no mocks. Pulled fresh from the platform every time you open this page."
        actions={
          <button data-testid="my-business-refresh" onClick={reload}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />

      {/* Headline card row */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white relative overflow-hidden" data-testid="mb-rent-card">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Rent collected · last 30 days</div>
            <div className="font-display text-4xl font-extrabold mt-2 tabular-nums">{formatCents(c.rent_collected_30d_cents)}</div>
            <div className="text-xs opacity-90 mt-1">{c.rent_payments_30d} payments · expected {formatCents(c.expected_monthly_cents)}/mo</div>
          </div>
        </Card>
        <Card className="p-6" data-testid="mb-occupancy-card">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Occupancy</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2 tabular-nums">{p.occupancy_pct}%</div>
          <div className="text-xs text-slate-500 mt-1">{p.active_leases} of {p.properties} leased · {p.vacant_units} vacant</div>
        </Card>
        <Card className="p-6" data-testid="mb-ontime-card">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">On-time rate · 30d</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2 tabular-nums">{c.on_time_pct}%</div>
          <div className="text-xs text-slate-500 mt-1">leases that paid this month</div>
        </Card>
      </div>

      <Section title="Portfolio" tone="emerald" testid="mb-section-portfolio">
        <Kpi testid="mb-kpi-properties" icon={Building2} label="Properties" value={p.properties} sub={`${p.active_leases} active leases`} />
        <Kpi testid="mb-kpi-vacant" icon={AlertCircle} label="Vacant units" value={p.vacant_units} tone={p.vacant_units > 0 ? "amber" : "emerald"} sub="ready to lease" />
        <Kpi testid="mb-kpi-expiring" icon={FileText} label="Leases ending · 30d" value={p.expiring_30d} sub="renewal window" tone={p.expiring_30d > 0 ? "sky" : "slate"} />
        <Kpi testid="mb-kpi-applications" icon={Users2} label="Open applications" value={p.open_applications} sub="awaiting your review" />
      </Section>

      <Section title="Cash flow" tone="emerald" testid="mb-section-cash">
        <Kpi testid="mb-kpi-stp" icon={Sparkles} label="STP rate · 30d" value={`${c.stp_rate_pct}%`} sub={`${c.cash_apps_auto_30d}/${c.cash_apps_total_30d} auto-applied`} />
        <Kpi testid="mb-kpi-pending" icon={AlertCircle} label="Pending review" value={c.cash_apps_pending_review} sub="cash-app exceptions" tone={c.cash_apps_pending_review > 0 ? "amber" : "slate"} />
        <Kpi testid="mb-kpi-expenses" icon={Wallet} label="Expenses · last 90d" value={formatCents(c.expenses_90d_cents)} sub="CRA-tracked" tone="rose" />
        <Kpi testid="mb-kpi-tickets" icon={Wrench} label="Open tickets" value={p.open_tickets} sub="maintenance" tone={p.open_tickets > 0 ? "amber" : "slate"} />
      </Section>

      <Section title="Smart engagement" tone="sky" testid="mb-section-engagement">
        <Kpi testid="mb-kpi-smart-asks" icon={Sparkles} label="Smart Op asks · 30d" value={e.smart_operator_asks_30d} sub="questions you asked" />
        <Kpi testid="mb-kpi-referrals" icon={Heart} label="Referrals paid" value={e.referrals_paid_total} sub="$50 CAD each" />
        <Kpi testid="mb-kpi-trend" icon={TrendingUp} label="Trend" value={c.on_time_pct >= 80 ? "Healthy" : c.on_time_pct >= 60 ? "Watch" : "Action"} sub="based on on-time rate" tone={c.on_time_pct >= 80 ? "emerald" : c.on_time_pct >= 60 ? "amber" : "rose"} />
        <Kpi testid="mb-kpi-checks" icon={CheckCircle2} label="Everything else" value="✓" sub="no critical alerts" />
      </Section>

      {series && <ChartCard rows={series.rows} />}
    </div>
  );
}

function Section({ title, tone, children, testid }) {
  const dots = { emerald: "bg-emerald-500", sky: "bg-sky-500", violet: "bg-violet-500", amber: "bg-amber-500" };
  return (
    <section className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1.5 h-1.5 rounded-full ${dots[tone] || dots.emerald}`} />
        <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-[0.16em]">{title}</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700", emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums truncate">{value ?? "—"}</div>
          {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-none ${tones[tone] || tones.slate}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
}

function ChartCard({ rows }) {
  const maxRent = Math.max(1, ...rows.map((r) => r.rent_cents || 0));
  return (
    <Card className="p-6" data-testid="mb-chart-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-base font-bold text-slate-900">Daily activity — last {rows.length} days</h3>
          <p className="text-xs text-slate-500 mt-0.5">Rent collected · new applications · Smart Operator asks</p>
        </div>
      </div>
      <div className="grid gap-px h-32 items-end" style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
        {rows.map((r) => {
          const h = Math.max(2, Math.round((r.rent_cents / maxRent) * 100));
          return (
            <div key={r.date} title={`${r.date}\n${formatCents(r.rent_cents)} rent\n${r.applications} applications\n${r.smart_op_asks} Smart Op`} className="flex flex-col-reverse h-full">
              <div className="bg-emerald-500/80 hover:bg-emerald-600 rounded-t-sm transition-colors" style={{ height: `${h}%` }} />
              {r.applications > 0 && <div className="bg-sky-400/70 rounded-t-sm" style={{ height: `${Math.min(20, r.applications * 4)}px` }} />}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-slate-400 font-medium">
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </Card>
  );
}
